// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  ClipboardList,
  BookOpen,
  ShieldCheck,
  Users,
  Settings,
  CloudSun,
  LogOut,
} from "lucide-react";
import "./KitchenShell.css";
import { useT } from "../i18n";

const NAV = [
  { id: "mep", label: "Mise en Place", Icon: ClipboardList },
  { id: "recepten", label: "Recepten", Icon: BookOpen },
  { id: "haccp", label: "HACCP", Icon: ShieldCheck },
  { id: "team", label: "Team", Icon: Users },
  { id: "settings", label: "Instellingen", Icon: Settings },
];

const SUPABASE_URL =
  import.meta.env?.VITE_SUPABASE_URL ||
  "https://vvwgplljrzeycxlorfli.supabase.co";
const SUPABASE_KEY =
  import.meta.env?.VITE_SUPABASE_KEY ||
  "sb_publishable_zETvKuK1K2Lhh2waE5uoPw_seq-Ptf_";

const WMO = {
  0: "Helder", 1: "Vrijwel onbewolkt", 2: "Half bewolkt", 3: "Bewolkt",
  45: "Mist", 48: "Mist", 51: "Lichte motregen", 53: "Motregen", 55: "Motregen",
  61: "Lichte regen", 63: "Regen", 65: "Zware regen", 71: "Lichte sneeuw",
  73: "Sneeuw", 75: "Zware sneeuw", 80: "Lichte buien", 81: "Buien", 82: "Zware buien",
  95: "Onweer", 96: "Onweer met hagel", 99: "Onweer met hagel",
};

function SidebarWeather({ place }) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!place) { setWeather(null); return; }
    let actief = true;
    (async () => {
      try {
        const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(place)}&count=1&language=nl`);
        const geo = await geoRes.json();
        const hit = geo?.results?.[0];
        if (!hit) return;
        const wRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${hit.latitude}&longitude=${hit.longitude}&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=1`);
        const w = await wRes.json();
        if (!actief) return;
        setWeather({
          place: hit.name || place,
          temperature: Math.round(w?.current?.temperature_2m),
          description: WMO[w?.current?.weather_code] || "Weer",
          max: Math.round(w?.daily?.temperature_2m_max?.[0]),
          min: Math.round(w?.daily?.temperature_2m_min?.[0]),
        });
      } catch {
        if (actief) setWeather(null);
      }
    })();
    return () => { actief = false; };
  }, [place]);

  return (
    <div className="kc-sidebar__weather">
      <div className="kc-sidebar__weather-top">
        <CloudSun size={19} />
        <div className="kc-sidebar__weather-temp">
          {weather?.temperature !== undefined && weather?.temperature !== null ? `${weather.temperature}°` : "--°"}
        </div>
      </div>
      <div className="kc-sidebar__weather-place">{weather?.place || place || "Weer"}</div>
      <div className="kc-sidebar__weather-detail">
        {weather ? `${weather.description} · ${weather.min}° / ${weather.max}°` : "Locatie instellen via Instellingen"}
      </div>
    </div>
  );
}

function useLogout() {
  const authClient = useMemo(() => createClient(SUPABASE_URL, SUPABASE_KEY), []);
  return async () => {
    try {
      await authClient.auth.signOut();
    } finally {
      window.location.reload();
    }
  };
}

export default function KitchenSidebar({
  activeModule,
  onNavigate,
  bedrijfsnaam = "KitchenControl",
  logoUrl = "/kc-logo.png",
  weatherPlace = "",
}) {
  const logout = useLogout();
  const { t } = useT();
  return (
    <aside className="kc-sidebar" aria-label="Hoofdnavigatie">
      <button type="button" className="kc-sidebar__brand" onClick={() => onNavigate?.(null)}>
        <img src={logoUrl || "/kc-logo.png"} alt="" />
        <span>{bedrijfsnaam}</span>
      </button>

      <nav className="kc-sidebar__nav">
        {NAV.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            className={`kc-sidebar__item${activeModule === id ? " is-active" : ""}`}
            onClick={() => onNavigate?.(id)}
          >
            <Icon size={19} strokeWidth={2} />
            <span>{t(label)}</span>
          </button>
        ))}
      </nav>

      <div className="kc-sidebar__footer">
        <button type="button" className="kc-sidebar__item" onClick={logout} title="Uitloggen">
          <LogOut size={19} strokeWidth={2} />
          <span>{t("Uitloggen")}</span>
        </button>
        <SidebarWeather place={weatherPlace} />
      </div>
    </aside>
  );
}

export function KitchenMobileNav({ activeModule, onNavigate }) {
  const logout = useLogout();
  const { t } = useT();
  return (
    <nav className="kc-mobile-nav" aria-label="Mobiele hoofdnavigatie" style={{ gridTemplateColumns: "repeat(6, 1fr)" }}>
      {NAV.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          className={activeModule === id ? "is-active" : ""}
          onClick={() => onNavigate?.(id)}
        >
          <Icon size={18} />
          <span>{label === "Mise en Place" ? "MEP" : t(label)}</span>
        </button>
      ))}
      <button type="button" onClick={logout} title="Uitloggen">
        <LogOut size={18} />
        <span>{t("Uit")}</span>
      </button>
    </nav>
  );
}
